import React from 'react';
import { Moon, Clock, Wind, Flame, Heart, Activity } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

const ZONE_COLORS = {
  z1: '#06b6d4', z2: '#10b981', z3: '#f59e0b', z4: '#FC4C02', z5: '#ef4444'
};
const ZONE_LABELS = {
  z1: 'Z1 Recuperación', z2: 'Z2 Resistencia', z3: 'Z3 Ritmo',
  z4: 'Z4 Umbral', z5: 'Z5 Anaeróbico'
};
const ZONE_DESC = {
  z1: '< 114 bpm', z2: '115–125 bpm', z3: '126–136 bpm',
  z4: '137–150 bpm', z5: '> 151 bpm'
};

const extractVal = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.value ?? val.val ?? val.current ?? null;
  return val;
};

const RecoveryPanel = ({ data }) => {
  if (!data || data.error) return null;

  const { streak, sleepData, hrvData, rhrData, withingsConnected } = data;

  const streakColor = streak > 6 ? '#ef4444' : streak > 4 ? '#f59e0b' : '#10b981';

  // Priorizar duración oficial si está presente, sino sumar fases
  const totalSleepDuration = sleepData?.duration != null
    ? parseFloat(sleepData.duration).toFixed(1)
    : (sleepData?.stages && (sleepData.stages.deep || sleepData.stages.light || sleepData.stages.rem)
      ? (sleepData.stages.deep + sleepData.stages.light + sleepData.stages.rem).toFixed(1)
      : '--');

  const sleepScore = extractVal(sleepData?.currentScore);
  const rhrCurrent = extractVal(rhrData?.current);
  const rhrAvg = extractVal(rhrData?.average) || 60;
  const hrvCurrent = extractVal(hrvData?.current);
  const hrvAvg = extractVal(hrvData?.average) || 0;
  const breathCurrent = extractVal(sleepData?.breathingRate?.current);

  return (
    <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '1.5rem' }}>
      
      {/* HEADER */}
      <div style={{ position: 'relative', zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Moon size={20} style={{ color: 'var(--health-cyan)' }} /> Calidad del Sueño y Descanso
            <InfoTooltip position="bottom" align="right" text="Muestra el desglose de fases de tu sueño nocturno, respiración promedio y horas totales descansadas. Esencial para la regeneración muscular y del sistema nervioso." />
          </h2>
          <p className="text-muted text-xs">Análisis nocturno y fases del sueño</p>
          {data?.isSimulatedToday && (
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#f59e0b', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(245, 158, 11, 0.1)', padding: '0.3rem 0.6rem', borderRadius: '4px' }}>
              ⚠️ Datos estimados (ayer). Si usas Apple Health, abre la App de móvil para sincronizar los datos de hoy.
            </div>
          )}
        </div>
        {withingsConnected && (
          <span style={{ 
            fontSize: '0.65rem', 
            background: 'rgba(6,182,212,0.15)', 
            color: 'var(--health-cyan)', 
            padding: '3px 8px', 
            borderRadius: '6px', 
            fontWeight: 600 
          }}>
            Biometría Activa
          </span>
        )}
      </div>

      {/* KPIs DE RECUPERACIÓN — Premium gradient cards */}
      <div className="metric-cards-grid">
        {[
          { 
            label: 'Duración Sueño', 
            value: `${totalSleepDuration}`, 
            icon: <Clock size={14} />, 
            unit: 'h',
            accent: '#00e5ff'
          },
          { 
            label: 'Calidad Sueño', 
            value: sleepScore !== null ? sleepScore : '--', 
            icon: <Moon size={14} />, 
            unit: '/100',
            accent: sleepScore >= 70 ? '#8b5cf6' : (sleepScore >= 50 ? '#f59e0b' : '#ef4444')
          },
          { 
            label: 'FC Reposo', 
            value: `${rhrCurrent !== null ? rhrCurrent : '--'}`, 
            icon: <Heart size={14} />, 
            unit: 'ppm',
            accent: rhrCurrent !== null && rhrCurrent <= rhrAvg ? '#10b981' : '#ef4444'
          },
          { 
            label: 'HRV rMSSD', 
            value: `${hrvCurrent !== null ? Math.round(hrvCurrent) : '--'}`, 
            icon: <Activity size={14} />, 
            unit: 'ms',
            accent: hrvCurrent !== null && hrvCurrent >= hrvAvg ? '#10b981' : '#f59e0b'
          },
          { 
            label: 'Ritmo Resp.', 
            value: breathCurrent !== null ? breathCurrent : '--', 
            icon: <Wind size={14} />, 
            unit: 'rpm',
            accent: '#10b981'
          }
        ].map(({ label, value, icon, unit, accent }) => (
          <div key={label} style={{
            background: `radial-gradient(ellipse at top left, ${accent}18 0%, ${accent}05 60%, transparent 100%), rgba(10,15,25,0.7)`,
            border: `1px solid ${accent}30`,
            borderRadius: '14px',
            padding: '1rem 1.1rem',
            display: 'flex', flexDirection: 'column', gap: '6px',
            boxShadow: `0 4px 20px ${accent}10, inset 0 1px 0 ${accent}15`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
              <span style={{ color: accent, opacity: 0.8 }}>{icon}</span>
            </div>
            <p style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', lineHeight: 1, margin: '4px 0 0', fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>
              {value}<span style={{ fontSize: '1rem', color: accent, fontWeight: 600, marginLeft: '3px' }}>{unit}</span>
            </p>
          </div>
        ))}
      </div>

      {/* 😴 DESGLOSE DE FASES DE SUEÑO (WITHINGS) */}
      {sleepData?.stages && (sleepData.stages.deep || sleepData.stages.light || sleepData.stages.rem) ? (
        <div style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '0.85rem 1rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.6rem', fontWeight: 600 }}>
            Desglose de Fases de Sueño (Última Noche)
          </p>
          
          {/* Segmented bar */}
          <div style={{ 
            width: '100%', height: '36px', background: 'rgba(255,255,255,0.04)', 
            borderRadius: '8px', overflow: 'hidden', display: 'flex', marginBottom: '1.25rem' 
          }}>
            {[
              { key: 'deep',  val: sleepData.stages.deep,  color: '#06b6d4' },
              { key: 'light', val: sleepData.stages.light, color: '#10b981' },
              { key: 'rem',   val: sleepData.stages.rem,   color: '#8b5cf6' },
              { key: 'awake', val: sleepData.stages.awake, color: '#f59e0b' }
            ].map(({ key, val, color }) => {
              const totalTime = sleepData.stages.deep + sleepData.stages.light + sleepData.stages.rem + sleepData.stages.awake || 1;
              const pct = (val / totalTime) * 100;
              return val > 0 ? (
                <div 
                  key={key} 
                  title={`${key}: ${val}h (${Math.round(pct)}%)`}
                  style={{ 
                    height: '100%', width: `${pct}%`, background: color, transition: 'width 0.5s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: '0.8rem', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.6)'
                  }} 
                >
                  {pct >= 10 && `${Math.round(pct)}%`}
                </div>
              ) : null;
            })}
          </div>

          {/* Leyenda de fases */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem 0.75rem' }}>
            {[
              { label: 'Profundo (Físico)', val: `${sleepData.stages.deep}h`, color: '#06b6d4', desc: 'Recuperación de fibras' },
              { label: 'REM (Neural)', val: `${sleepData.stages.rem}h`, color: '#8b5cf6', desc: 'Asimilación mental' },
              { label: 'Ligero', val: `${sleepData.stages.light}h`, color: '#10b981', desc: 'Transición/Descanso' },
              { label: 'Despierto', val: `${sleepData.stages.awake}h`, color: '#f59e0b', desc: 'Interrupciones nocturnas' }
            ].map(({ label, val, color, desc }) => (
              <div key={label} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, marginTop: '4px' }} />
                <div>
                  <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'white', lineHeight: '1.2' }}>{label} <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>· {val}</span></p>
                  <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.2', marginTop: '2px' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Faltan datos de fases de sueño.</p>
        </div>
      )}
    </div>
  );
};

export default RecoveryPanel;
